import { useState } from "react";
import { breakdown } from "@/lib/run-metrics";
import { RUN_COLORS } from "@/lib/run-colors";
import type { RunDetail } from "@/api/types";

interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
  hint?: string;
}

// Human labels for the structured `unreachable_reason` codes. Codes the
// API emits but we haven't labelled yet pass through verbatim — better
// readable-ish than dropped.
const REASON_LABELS: Record<string, string> = {
  dns: "DNS / NXDOMAIN",
  idn_encoding: "IDN encoding failure",
  bad_url: "Invalid URL",
  tcp_refused: "TCP refused",
  tcp_timeout: "Connect timeout",
  tcp_reset: "Connection reset",
  tls_handshake: "TLS handshake failed",
  read_timeout: "Read timeout",
  wall_timeout: "Worker wall timeout",
  chunked_encoding: "Chunked encoding broke",
  content_decoding: "Compression broke",
  http_403: "HTTP 403 (anti-bot)",
  http_429: "HTTP 429 (rate limited)",
  http_404: "HTTP 404 (gone)",
  http_4xx_other: "HTTP 4xx (other)",
  http_5xx: "HTTP 5xx",
  too_many_redirects: "Redirect loop",
  too_large: "Response too large",
  unknown: "Unclassified",
};

function reasonLabel(code: string): string {
  if (REASON_LABELS[code]) return REASON_LABELS[code];
  // Heavy-suffix variants — show the base label plus a hint that
  // the curl_cffi second-pass also failed.
  if (code.endsWith("_heavy")) {
    const base = code.slice(0, -"_heavy".length);
    return REASON_LABELS[base]
      ? `${REASON_LABELS[base]} (heavy-pass also)`
      : `${base} (heavy-pass also)`;
  }
  return code;
}

/**
 * Single horizontal stacked bar that, when `expected_total` is known,
 * accounts for every input URL: pushed | duplicate | tagged | no match |
 * unreachable | error | pending | pre-filtered | not yet read.
 *
 * Colors come from the shared `RUN_COLORS` palette so each bucket renders
 * the same shade in the progress chart and sankey too.
 *
 * `embedded` drops the outer card chrome so the bar can sit inside another
 * card (e.g. the active-run card on `/runs`) without nested borders. In
 * that mode the empty-state placeholder is suppressed too — the host card
 * already has its own content to show.
 */
export function OutcomeBar({
  run,
  embedded = false,
}: {
  run: RunDetail;
  embedded?: boolean;
}) {
  const br = breakdown(run);
  const [showReasons, setShowReasons] = useState(false);
  const byReason = run.progress.by_reason ?? {};
  const reasonRows = Object.entries(byReason)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const hasReasonBreakdown = br.unreachable > 0 && reasonRows.length > 0;

  const total =
    run.expected_total != null
      ? run.expected_total
      : br.completedTerminal + br.pending + br.preFiltered;
  const processed = br.completedTerminal + br.preFiltered;
  const processedPct = total > 0 ? (processed / total) * 100 : 0;
  if (total <= 0) {
    if (embedded) return null;
    return (
      <section className="card p-4 text-sm text-muted">
        Outcome breakdown will appear once the run produces its first
        results.
      </section>
    );
  }

  const segments: Segment[] = [
    {
      key: "pushed",
      label: "pushed",
      value: br.pushed,
      color: RUN_COLORS.pushed,
      hint: "New lead created in Close on this run.",
    },
    {
      key: "duplicate",
      label: "duplicate",
      value: br.duplicate,
      color: RUN_COLORS.duplicate,
      hint: "Extracted, but the lead already exists in Close — we deliberately did not push it.",
    },
    {
      key: "tagged",
      label: "tagged",
      value: br.tagged,
      color: RUN_COLORS.tagged,
      hint: "Matched a check but produced no lead — either a skip-action check fired, no Impressum was found, or the LLM rejected the verification.",
    },
    {
      key: "noMatch",
      label: "no match",
      value: br.noMatch,
      color: RUN_COLORS.noMatch,
      hint: "No check fired — the page didn't look like any configured shop or site type.",
    },
    {
      key: "unreachable",
      label: "unreachable",
      value: br.unreachable,
      color: RUN_COLORS.unreachable,
      hint: "Target server didn't respond — DNS, network, TLS, or wall-clock timeout. Not an application bug; the prospect's site is down or rate-limiting us.",
    },
    {
      key: "errored",
      label: "error",
      value: br.errored,
      color: RUN_COLORS.error,
      hint: "Internal failure during extraction — GPT crash, parser bug, or worker exception. Worth investigating when this is non-zero.",
    },
    {
      key: "pending",
      label: "pending",
      value: br.pending,
      color: RUN_COLORS.pending,
      hint: "Claimed by this run, currently in the dramatiq queue or being worked.",
    },
    {
      key: "preFiltered",
      label: "pre-filtered",
      value: br.preFiltered,
      color: RUN_COLORS.preFiltered,
      hint: "Producer dropped before claim — bad URL, ignored list, or already owned by another run.",
    },
    {
      key: "notYetRead",
      label: "not yet read",
      value: br.notYetRead,
      color: RUN_COLORS.notYetRead,
      hint: "Raw URLs the producer still has to read from the source.",
    },
  ];

  const visible = segments.filter((s) => s.value > 0);

  const body = (
    <>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold">Outcome breakdown</h2>
        <span className="text-xs text-muted tabular-nums">
          {processed.toLocaleString()} / {total.toLocaleString()} (
          {processedPct.toFixed(processedPct < 1 ? 2 : 1)}%)
        </span>
      </div>

      <div
        className="flex h-3 w-full overflow-hidden rounded-md border border-border"
        role="img"
        aria-label="Outcome breakdown"
      >
        {visible.map((s) => {
          const pct = (s.value / total) * 100;
          // Floor very thin segments to a minimum sliver so a 0.01%
          // segment is still visible/hoverable.
          const width = Math.max(pct, pct > 0 ? 0.4 : 0);
          return (
            <div
              key={s.key}
              style={{ width: `${width}%`, background: s.color }}
              title={`${s.label}: ${s.value.toLocaleString()} (${pct.toFixed(pct < 1 ? 2 : 1)}%)${
                s.hint ? "\n" + s.hint : ""
              }`}
            />
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {visible.map((s) => {
          const pct = (s.value / total) * 100;
          const isUnreachable = s.key === "unreachable";
          return (
            <div
              key={s.key}
              className="flex items-center gap-1.5"
              title={s.hint}
            >
              <span
                className="h-2 w-2 rounded-sm"
                style={{ background: s.color }}
              />
              <span className="text-muted">{s.label}</span>
              <span className="tabular-nums">{s.value.toLocaleString()}</span>
              <span className="text-muted tabular-nums">
                ({pct.toFixed(pct < 1 ? 2 : 1)}%)
              </span>
              {isUnreachable && hasReasonBreakdown && (
                <button
                  type="button"
                  className="ml-1 text-muted underline-offset-2 hover:underline"
                  onClick={() => setShowReasons((v) => !v)}
                  aria-expanded={showReasons}
                >
                  {showReasons ? "hide reasons" : "why?"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showReasons && hasReasonBreakdown && (
        <div className="mt-3 rounded-md border border-border bg-card-muted/50 p-3">
          <div className="mb-2 text-xs font-medium">
            Unreachable breakdown ({br.unreachable.toLocaleString()} total)
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {reasonRows.map(([code, n]) => {
              const pct = (n / br.unreachable) * 100;
              return (
                <div key={code} className="flex items-center justify-between">
                  <span className="text-muted">{reasonLabel(code)}</span>
                  <span className="ml-2 tabular-nums">
                    {n.toLocaleString()}
                    <span className="ml-1 text-muted">
                      ({pct.toFixed(pct < 1 ? 2 : 1)}%)
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return <div>{body}</div>;
  return <section className="card p-4">{body}</section>;
}
